/**
 * Gestionnaire de configuration de routage centralisé
 * Gère la structure JSON unifiée pour tous les headers de routage
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { encryptData, decryptData, isEncrypted } from './encrypt-service.js';
import { getItem, setItem, getItemEncrypted } from './encrypted-local-storage.js';

/**
 * Structure complète de la configuration de routage
 * @typedef {Object} RoutingConfig
 * @property {Object} text - Configuration pour les appels IA Text
 * @property {Object} vision - Configuration pour les appels IA Vision
 * @property {Object} translation - Configuration pour les appels IA Translation
 * @property {Object} wa - Configuration pour les appels WorldAnvil
 * @property {Object} _metadata - Métadonnées (timestamp, version)
 */

const CONFIG_KEY = 'routing_config';

/**
 * Crée une configuration de routage vierge
 * @returns {RoutingConfig}
 */
function createEmptyConfig() {
  return {
    text: {
      'X-API-Key': null,
      'X-Router': 'DUMMY',
      'X-Router-URL': 'https://dummy.api.url',
      'X-Model': 'DUMMY'
    },
    vision: {
      'X-API-Key': null,
      'X-Router': 'DUMMY',
      'X-Router-URL': 'https://dummy.api.url',
      'X-Model': 'DUMMY'
    },
    translation: {
      'X-API-Key': null,
      'X-Router': 'DUMMY',
      'X-Router-URL': 'https://dummy.api.url',
      'X-Model': null
    },
    wa: {
      'X-API-Key': null
    },
    _metadata: {
      timestamp: Date.now(),
      version: '2.0'
    }
  };
}

/**
 * Récupère la configuration de routage complète
 * Charge depuis le nouveau format uniquement, ignore les clés legacy
 * @returns {Promise<RoutingConfig>}
 */
export async function getRoutingConfig() {
  try {
    // Charger depuis le localStorage en clair
    const configJson = localStorage.getItem(CONFIG_KEY);
    
    if (configJson) {
      const config = JSON.parse(configJson);
      return config;
    }

    // Aucune configuration trouvée, retourner vierge
    return createEmptyConfig();
  } catch (error) {
    debug.error(config, 'Erreur lors de la récupération de la configuration de routage:', error);
    return createEmptyConfig();
  }
}

/**
 * Récupère la configuration de routage pour une section spécifique
 * @param {string} section - 'text', 'vision', 'translation', ou 'wa'
 * @returns {Promise<Object>}
 */
export async function getRoutingConfigSection(section) {
  const fullConfig = await getRoutingConfig();
  return fullConfig[section] || {};
}

/**
 * Sauvegarde la configuration de routage complète
 * @param {RoutingConfig} routingConfig
 * @returns {Promise<boolean>}
 */
export async function setRoutingConfig(routingConfig) {
  try {
    const updated = {
      ...routingConfig,
      _metadata: {
        ...routingConfig._metadata,
        timestamp: Date.now()
      }
    };

    // Stocker en clair (les clés API sont déjà chiffrées)
    localStorage.setItem(CONFIG_KEY, JSON.stringify(updated));
    debug.log(config, 'Configuration de routage sauvegardée');
    return true;
  } catch (error) {
    debug.error(config, 'Erreur lors de la sauvegarde de la configuration de routage:', error);
    return false;
  }
}

/**
 * Met à jour une section spécifique de la configuration
 * @param {string} section - 'text', 'vision', 'translation', ou 'wa'
 * @param {Object} sectionConfig - Configuration de la section
 * @returns {Promise<boolean>}
 */
export async function updateRoutingConfigSection(section, sectionConfig) {
  try {
    const fullConfig = await getRoutingConfig();
    fullConfig[section] = { ...fullConfig[section], ...sectionConfig };
    fullConfig._metadata.timestamp = Date.now();
    return await setRoutingConfig(fullConfig);
  } catch (error) {
    debug.error(config, `Erreur lors de la mise à jour de la section ${section}:`, error);
    return false;
  }
}

/**
 * Met à jour une clé API spécifique
 * @param {string} section - 'text', 'vision', 'translation', ou 'wa'
 * @param {string} apiKey - Clé API (sera chiffrée)
 * @returns {Promise<boolean>}
 */
export async function updateApiKey(section, apiKey) {
  try {
    const fullConfig = await getRoutingConfig();
    
    if (!fullConfig[section]) {
      fullConfig[section] = {};
    }

    // Chiffrer la clé API
    const encryptedKey = await encryptData(apiKey);
    fullConfig[section]['X-API-Key'] = encryptedKey;
    fullConfig._metadata.timestamp = Date.now();

    return await setRoutingConfig(fullConfig);
  } catch (error) {
    debug.error(config, `Erreur lors de la mise à jour de la clé API pour ${section}:`, error);
    return false;
  }
}

/**
 * Exporte la configuration pour sauvegarde (utilisé par config-import-export.js)
 * @returns {Promise<Object>}
 */
export async function exportRoutingConfig() {
  try {
    const config = await getRoutingConfig();
    return {
      routing_config: config,
      _exported_at: new Date().toISOString()
    };
  } catch (error) {
    debug.error(config, 'Erreur lors de l\'export de la configuration de routage:', error);
    return {};
  }
}

/**
 * Importe la configuration depuis une sauvegarde
 * @param {Object} exportedData
 * @returns {Promise<boolean>}
 */
export async function importRoutingConfig(exportedData) {
  try {
    if (exportedData.routing_config) {
      return await setRoutingConfig(exportedData.routing_config);
    }
    return false;
  } catch (error) {
    debug.error(config, 'Erreur lors de l\'import de la configuration de routage:', error);
    return false;
  }
}

/**
 * Récupère la configuration chiffrée brute (pour les headers)
 * @returns {Promise<string|null>}
 */
export async function getRoutingConfigEncrypted() {
  try {
    const fullConfig = await getRoutingConfig();
    return await encryptData(fullConfig);
  } catch (error) {
    debug.error(config, 'Erreur lors du chiffrage de la configuration de routage:', error);
    return null;
  }
}

/**
 * Récupère la langue de travail IA depuis routing_config
 * Source unique de vérité pour la langue de travail
 * 
 * @returns {Promise<string|null>} - Code langue de travail (EN, FR, etc.) ou null
 */
export async function getAiWorkingLanguage() {
  try {
    const textConfig = await getRoutingConfigSection('text');
    return textConfig?.['X-Working-Language'] || null;
  } catch (error) {
    debug.error(config, 'Erreur lors du chargement de la langue de travail IA:', error);
    return null;
  }
}

/**
 * Définit la langue de travail IA dans routing_config
 * 
 * @param {string} language - Code langue (EN, FR, etc.)
 * @returns {Promise<boolean>} - true si succès
 */
export async function setAiWorkingLanguage(language) {
  try {
    await updateRoutingConfigSection('text', {
      'X-Working-Language': language
    });
    debug.log(config, `Langue de travail IA définie: ${language}`);
    return true;
  } catch (error) {
    debug.error(config, 'Erreur lors de la définition de la langue de travail IA:', error);
    return false;
  }
}

export default {
  getRoutingConfig,
  getRoutingConfigSection,
  setRoutingConfig,
  updateRoutingConfigSection,
  updateApiKey,
  exportRoutingConfig,
  importRoutingConfig,
  getRoutingConfigEncrypted,
  createEmptyConfig,
  getAiWorkingLanguage,
  setAiWorkingLanguage
};
